import Cookies from 'js-cookie';
import PubSub from 'pubsub-js';
import hive from '@bombas/hive';
import {
  convertGlobalIdToRawId,
  convertVariantIdToGlobalId,
  GraphQL
} from '@bombas/shopify';
import client from '../client';
import {
  CART_CREATE,
  CART_DISCOUNT_CODE_UPDATE,
  CART_LINES_ADD,
  CART_LINES_REMOVE,
  CART_LINES_UPDATE,
  CART_ATTRIBUTES_UPDATE,
  CART_NOTE_UPDATE,
  UPDATE_CART_BUYER_IDENTITY
} from './mutations';
import { getCookieDomain, insertIf, withMutex } from '../../../util/util';
import { getUserCountry } from '../../../util/localize';

import { QBS_ENABLED } from '../../../util/const';
import { PRODUCT_VARIANT_COUNT } from '../../../components/Navigation/Navigation.gql';
import { NOTE } from '../../../components/Checkout/components/GiftingContainer/GiftingContainer.gql';
import {
  CURRENT_USER,
  DISCOUNT_ALLOCATIONS,
  GET_USER_LOCALE
} from '../../../components/App.gql';
import { checkIsAttributable } from '../../../util/rebuyAttribution';

export const CART_ID_COOKIE = 'BOM-CART_ID';

/**
 * Publishes a cartUpdate event and passes through the result; for use as a `then` handler.
 * @param {*} result Proxied result
 * @return {*} result
 */
export const proxyPublishCartUpdate = (result) => {
  PubSub.publish('cart.update');
  return result;
};

/**
 * Retrieves the data from an unauthenticated cart in preparation to migrate its data to a new cart.
 * @param {ApolloClient} apolloClient
 * @param {String} cartId Shopify Cart ID
 * @param {String} country Country code
 * @returns {Promise<null | object>} Resolves with cart data or null if no cart found.
 */
const getExistingCart = async (apolloClient, cartId, country) => {
  const result = {};
  const { data: existingCart } = await apolloClient.query({
    query: GraphQL.CART,
    variables: { cartId, country },
    context: { service: 'storefront' }
  });

  if (!existingCart?.cart) return null;

  result.attributes = existingCart.cart.attributes.map((attr) => ({
    key: attr.key,
    value: attr.value
  }));
  result.buyerIdentity = {
    email: existingCart.cart.buyerIdentity.email,
    countryCode: existingCart.cart.buyerIdentity.countryCode
  };
  result.lines = existingCart.cart.lines.edges.map((line) => ({
    merchandiseId: line.node.merchandise.id,
    quantity: line.node.quantity,
    attributes: line.node.attributes.map((attr) => ({
      key: attr.key,
      value: attr.value
    }))
  }));
  result.discountCodes = existingCart.cart.discountAllocations.map(
    (discount) => discount.code
  );

  return result;
};

/**
 * Resets the customers cart by removing the cookie and optionally triggering a UI refresh.
 */
export const resetCart = (skipUpdate = false) => {
  Cookies.remove(CART_ID_COOKIE, { domain: getCookieDomain() });
  if (!skipUpdate) proxyPublishCartUpdate();
};

/**
 * Creates a Storefront request handler with a root accessor.
 * @deprecated This approach should not be necessary for @apollo implementation. Handle specific storefront errors with an Error link in `/graphql/client.js'. https://www.apollographql.com/docs/react/api/link/apollo-link-error/
 * @param {String} key Root key accessor in expected response
 * @returns {Promise} Resolves with response in key, rejects with error.
 */
export const handleStorefrontRequest =
  (key, attr = {}) =>
  (result) => {
    const data = result?.[key];

    const customAttributes = { location: 'services/storefront' };

    if (key === 'cart' && data === null) {
      resetCart();

      const cartError = new Error(
        'Cart - Unknown cart error. Storefront returned null when fetching for Cart information using BOM-CART_ID.'
      );
      window.DD_RUM?.addError(cartError, {
        query: key,
        errorContext: 'handleStorefrontRequest - Unknown Cart Error',
        ...attr,
        ...customAttributes
      });
      return Promise.reject(cartError);
    }

    if (data?.userErrors?.length) {
      data.userErrors.forEach((userError) => {
        const error = new Error(`Cart - ${userError?.message}`);

        window.DD_RUM?.addError(error, {
          query: key,
          errorContext:
            'handleStorefrontRequest - User errors returned in response',
          ...attr,
          ...customAttributes
        });
      });

      return Promise.reject(
        new Error('Cart - User errors returned in response.')
      );
    }

    if (data?.cart) return Promise.resolve(data.cart);

    if (data) return Promise.resolve(data);

    const genericError = new Error(
      'Cart - An unknown error has occurred making a request to Storefront.'
    );
    window.DD_RUM?.addError(genericError, {
      query: key,
      errorContext: 'handleStorefrontRequest - Generic Error',
      ...attr,
      ...customAttributes
    });
    return Promise.reject(genericError);
  };

const CART_ID_PROCESSED_KEY = 'BOM-CART_ID_PROCESSED';

/**
 * This is the private implementation of getCartId and should never be used directly.
 * Please use getCartId instead as it is wrapped with a mutex to prevent race conditions.
 */
const getCartIdImplementation = async (
  apolloClient,
  create = true,
  ignore = false
) => {
  if (create && !apolloClient) {
    throw new Error(
      'getCartIdImplementation - apolloClient is required when create = true'
    );
  }

  const params = new URLSearchParams(window.location.search);
  const cartIdParam = params.get('cartId');
  const cartId =
    (!window.sessionStorage.getItem(CART_ID_PROCESSED_KEY) && cartIdParam) ||
    Cookies.get(CART_ID_COOKIE);

  // Due to issues trying to replace history state and the query parameter restoring itself, we
  // will store a flag in the session to indicate we've already processed the cartId param.
  if (cartIdParam) {
    window.sessionStorage.setItem(CART_ID_PROCESSED_KEY, 1);
  }

  // This is a naive implementation that assumes the key param itself is valid.
  // We capture occurences of this in RUM to see if we need to enhance validation.
  const isValidCartId = cartId?.includes('?key');

  if (isValidCartId && !ignore) {
    // Extend cookie expiration 14 days from now.
    Cookies.set(CART_ID_COOKIE, cartId, {
      domain: getCookieDomain(),
      expires: 14
    });

    return cartId;
  }

  if (!create) {
    return false;
  }

  const landingSiteUrl = Cookies.get('BOM-LANDING_SITE');
  const mutationVariables = {
    input: {
      attributes: [
        ...insertIf(landingSiteUrl, [
          { key: 'landing_site', value: landingSiteUrl }
        ]),
        { key: '_disableDiscount', value: QBS_ENABLED ? 'false' : 'true' }
      ]
    }
  };

  const { user } = apolloClient.readQuery({
    query: GET_USER_LOCALE
  });

  const userCountry = user?.locale?.country;

  if (userCountry) {
    mutationVariables.input.buyerIdentity = {
      countryCode: userCountry
    };
  }

  // Only clone the cart if we have an existing cart ID that is invalid.
  if (cartId && !isValidCartId) {
    const existingCartData = await getExistingCart(
      apolloClient,
      cartId,
      userCountry
    );

    if (existingCartData) {
      mutationVariables.input = existingCartData;
    }
  }

  const result = await client
    .request(CART_CREATE, {
      ...mutationVariables,
      country: userCountry
    })
    .then(handleStorefrontRequest('cartCreate'))
    .catch((e) => {
      // Only add DD log if it's NOT handled by handleStorefrontRequest (i.e. client.request throws an error)
      // Otherwise, we end up with duplicate error logs in DD
      if (!e?.message?.startsWith('Cart -')) {
        window.DD_RUM?.addError(e, {
          query: 'cartCreate',
          errorContext:
            'getCartIdImplementation - Initial graphql client.request failed and error caught in catch, bypassing `handleStorefrontRequest`',
          location: 'services/storefront'
        });
      }
      return Promise.reject(new Error(e));
    });

  if (result?.id) {
    const { id } = result;
    Cookies.set(CART_ID_COOKIE, id, {
      domain: getCookieDomain(),
      expires: 14
    });
    return id;
  }

  throw new Error('getCartIdImplementation - Missing cart id.');
};

/**
 * Gets the value of the BOM-CART_ID cookie, refreshes its expiration date (+14 days); optionally creates a cart ID if it doesn't exist.
 * @param apolloClient apolloClient instance
 * @param {Boolean} create If true, creates a cart id cookie if it doesnt exist.
 * @param {Boolean} ignore If true, ignores any existing cart id.
 * @returns {Promise} Resolves with cart ID (or false if create = false), rejects if error trying to create cart ID.
 */
export const getCartId = withMutex(getCartIdImplementation);

export const updateCartCountry = async (country) => {
  // Pass null for apolloClient since we're only reading existing cart ID (create = false)
  const cartId = await getCartId(null, false);

  if (!cartId) return null;

  const results = await client
    .request(UPDATE_CART_BUYER_IDENTITY, {
      buyerIdentity: {
        countryCode: country
      },
      cartId
    })
    .catch((e) => {
      window.DD_RUM?.onReady(() => {
        window.DD_RUM?.addError(e, {
          query: 'cartBuyerIdentityUpdate',
          errorContext:
            'updateCartCountry - Initial graphql client.request failed and error caught in catch',
          location: 'services/storefront'
        });
      });
    });

  const cartBuyerIdentityUpdate = results?.cartBuyerIdentityUpdate;

  if (cartBuyerIdentityUpdate?.userErrors.length) {
    const { message } = cartBuyerIdentityUpdate.userErrors[0];
    window.DD_RUM?.onReady(() => {
      window.DD_RUM?.addError(new Error(`Cart - ${message}`), {
        query: 'cartBuyerIdentityUpdate',
        errorContext: 'updateCartCountry - User errors returned in response',
        location: 'services/storefront'
      });
    });
  }
};

/**
 * Performs a single field GraphQL query and automatically retries once with a new cart ID if the
 * original request has an invalid cart ID.
 * @param  {...any} params Parameters forwarded to `client.request`
 * @param apolloClient apollo client
 * @returns {Promise} Result
 */
export const safeRequest = (doc, vars, apolloClient) => {
  if (!doc)
    return Promise.reject(new Error('safeRequest - Missing `doc` value.'));

  return client
    .request(doc, vars)
    .then(async (result) => {
      const data = result?.[Object.keys(result)?.[0]];
      /** returns the name of the called query operation */
      const storefrontRequest = doc?.definitions[0]?.name?.value;

      if (data?.userErrors?.length) {
        const error = new Error(`Cart - ${data.userErrors[0]?.message}`);

        // Returns a new cart when Storefront throws an INVALID cart ID error
        if (
          data.userErrors.some(
            (cartError) =>
              cartError.code === 'INVALID' && cartError.field.includes('cartId')
          )
        ) {
          window.DD_RUM?.onReady(() => {
            window.DD_RUM?.addError(error, {
              query: storefrontRequest,
              errorContext:
                'safeRequest - Invalid cart ID; retrying with new cart ID',
              location: 'services/storefront'
            });
          });
          const cartId = await getCartId(apolloClient, true, true);

          return client.request(doc, { ...vars, cartId });
        }

        const products = vars?.lines;

        window.DD_RUM?.onReady(() => {
          window.DD_RUM?.addError(error, {
            query: storefrontRequest,
            errorContext: 'safeRequest - User errors returned in response',
            location: 'services/storefront',
            products
          });
        });

        return Promise.reject(error);
      }

      return result;
    })
    .catch((error) => {
      const safeRequestError = new Error(`safeRequest - ${error?.message}`);

      // log server errors from storefront api (timeouts, throttles, internal errors, etc)
      window.DD_RUM?.onReady(() => {
        window.DD_RUM?.addError(safeRequestError, {
          query: doc?.definitions[0]?.name?.value,
          errorContext:
            'safeRequest - Request failed and error caught in catch',
          location: 'services/storefront'
        });
      });

      return Promise.reject(safeRequestError);
    });
};

/**
 * Retrieves the contents of the cart.
 * @returns {Promise} Resolves with cart contents or null; rejects if cart error.
 */
export const getCart = async (apolloClient) => {
  // Removes old Checkout ID value in BOM-CART_ID so that it can be replaced with the Cart ID
  if (Cookies.get(CART_ID_COOKIE)?.includes('Checkout')) {
    resetCart();
  }

  const cartId = await getCartId(apolloClient);

  apolloClient.writeQuery({
    query: CURRENT_USER,
    data: {
      cartId
    }
  });

  const country = getUserCountry(apolloClient);
  return safeRequest(GraphQL.CART, { cartId, country }, apolloClient)
    .then(handleStorefrontRequest('cart'))
    .then((res) => {
      // write line item quantities to apollo cache to update Navigation cart item count
      apolloClient.writeQuery({
        query: PRODUCT_VARIANT_COUNT,
        data: {
          cart: {
            __typename: 'Cart',
            id: res.id,
            lines: res.lines
          }
        },
        variables: {
          cartId
        }
      });

      return Promise.resolve(res);
    });
};

/**
 * Add a product variant to cart.
 * @param {Number} variantId ProductVariant identifier
 * @param {Number} quantity ProductVariant quantity
 * @param {string[]} category Product category/categories
 * @param {Object} pimData Product PIM data
 * @param apolloClient apollo client
 * @returns {Promise} Resolves with updated Cart contents; rejects if Cart error.
 */
export const addToCart = async (
  variantId,
  quantity,
  pimData,
  customAttributes = [],
  apolloClient,
  productId,
  source
) => {
  const cartId = await getCartId(apolloClient);
  const attributionSource = checkIsAttributable(productId);

  customAttributes.push({
    key: '_pimData',
    value: JSON.stringify({
      variantId,
      ...pimData
    })
  });

  if (attributionSource) {
    hive.track('REBUY_UPSELL_CART_ADD', {
      sourceId: attributionSource
    });
    customAttributes.push(
      { key: '_source', value: 'Rebuy' },
      { key: '_attribution', value: attributionSource }
    );
  }

  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_LINES_ADD,
    {
      cartId,
      lines: [
        {
          merchandiseId: convertVariantIdToGlobalId(variantId),
          quantity,
          attributes: customAttributes
        }
      ],
      country
    },
    apolloClient
  )
    .then(proxyPublishCartUpdate)
    .then(handleStorefrontRequest('cartLinesAdd', { variantId }))
    .then(async (result) => {
      await hive.track('CART_ADD', {
        variantId,
        quantity,
        cart: { id: cartId, country },
        source
      });
      return result;
    });
};

/**
 * Add a multiple variant IDs to a cart.
 * The sum of ocurrences of a given variant ID is used to indicate quantity.
 * @param apolloClient apolloClient
 * @param {...Number} variantId ProductVariant identifier
 * @returns {Promise} Resolves with updated Cart contents; rejects if Cart error.
 */
export const bulkAddToCart = async (apolloClient, ...variantIds) => {
  if (!variantIds) return;

  const cartId = await getCartId(apolloClient);
  const lineItems = variantIds.reduce((result, variantId) => {
    result[variantId] = (result[variantId] || 0) + 1; // eslint-disable-line no-param-reassign
    return result;
  }, {});
  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_LINES_ADD,
    {
      cartId,
      lines: Object.keys(lineItems).map((id) => ({
        merchandiseId: convertVariantIdToGlobalId(id),
        quantity: lineItems[id]
      })),
      country
    },
    apolloClient
  )
    .then(proxyPublishCartUpdate)
    .then(handleStorefrontRequest('cartLinesAdd', { variantIds }));
};

/**
 * Remove line item(s) from cart.
 * @param apolloClient apollo client
 * @param  {...String} lines Base64 encoded namespaced global ID(s) to remove
 * @returns {Promise} Returns cart contents if no error, otherwise return null.
 */
export const removeFromCart = async (apolloClient, ...lines) => {
  const cartId = await getCartId(apolloClient, false);
  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_LINES_REMOVE,
    {
      cartId,
      lines,
      country
    },
    apolloClient
  )
    .then(proxyPublishCartUpdate)
    .then(handleStorefrontRequest('cartLinesRemove', { lines }));
};

/**
 * Update line item quantity from cart.
 * @param {String} lineItemId Base64 encoded namespaced global ID to update
 * @param {Number} quantity New quantity
 * @param {Number} variantId Base64 encoded namespaced variant ID
 * @param {Number} oldQuantity previous quantity
 * @param apolloClient apollo client
 * @returns {null} Returns cart contents if no error, otherwise return null.
 */
export const updateItemQuantity = async (
  lineItemId,
  quantity,
  variantId,
  oldQuantity,
  apolloClient
) => {
  const cartId = await getCartId(apolloClient, false);

  const country = getUserCountry(apolloClient);
  return safeRequest(
    CART_LINES_UPDATE,
    {
      cartId,
      lines: [{ id: lineItemId, merchandiseId: variantId, quantity }],
      country
    },
    apolloClient
  )
    .then(proxyPublishCartUpdate)
    .then(handleStorefrontRequest('cartLinesUpdate', { variantId }))
    .then(async (result) => {
      if (quantity === 0) {
        await hive.track('CART_REMOVE', {
          variantId: convertGlobalIdToRawId(variantId),
          quantity: oldQuantity,
          cart: { id: cartId, country }
        });
      } else if (quantity > oldQuantity) {
        await hive.track('CART_INCREMENT', {
          variantId: convertGlobalIdToRawId(variantId),
          quantity,
          cart: { id: cartId, country }
        });
      }
      return result;
    });
};

/**
 * Updates Apollo cache after a discount is removed or added.
 * The cache is used in DiscountAppliedMessage, CouponDialog, and Footer.
 *
 * @param {Object} res GQL Response returned from cartDiscountCodeUpdate mutation
 * @param {String} cartId Shopify Cart ID
 * @param {} apolloClient Apollo Client
 */
export const updateCachedDiscount = (res, cartId, apolloClient) => {
  const { discountAllocations, discountCodes } = res;

  // write updated discounts to apollo cache to update Footer
  apolloClient.writeQuery({
    query: CURRENT_USER,
    data: {
      cartId
    }
  });

  apolloClient.writeQuery({
    query: DISCOUNT_ALLOCATIONS,
    data: {
      cart: {
        __typename: 'Cart',
        id: res.id,
        discountAllocations,
        discountCodes
      }
    },
    variables: {
      cartId
    }
  });
};

/**
 * Apply a discount code to a new or existing cart.
 * @param {String[]} discountCodes Array of discount codes to pass into CartDiscountCodesUpdate mutation
 * @returns {Promise} Resolves with updated Cart contents; rejects if Cart error / invalid code.
 */
export const applyDiscountCode = async (discountCodes, apolloClient) => {
  const cartId = await getCartId(apolloClient);
  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_DISCOUNT_CODE_UPDATE,
    {
      cartId,
      discountCodes,
      country
    },
    apolloClient
  )
    .then(handleStorefrontRequest('cartDiscountCodesUpdate'))
    .then((res) => {
      hive.track('DISCOUNT_CODE_APPLY', {
        ecommerce: { discount_code: discountCodes[0] }
      });
      updateCachedDiscount(res, cartId, apolloClient);
      return res;
    });
};

/**
 * There is no specific query to strictly remove a discount code.
 * Therefore, we must pass in an empty array in the discountCode argument to mimic the removal of discount codes.
 */
export const removeDiscountCode = async (apolloClient) => {
  const cartId = await getCartId(apolloClient);
  // Empty array to mock no discounts/discount removal
  const discountCodes = [];
  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_DISCOUNT_CODE_UPDATE,
    {
      cartId,
      discountCodes,
      country
    },
    apolloClient
  )
    .then(handleStorefrontRequest('cartDiscountCodesUpdate'))
    .then((res) => {
      updateCachedDiscount(res, cartId, apolloClient);
      return res;
    })
    .catch((e) => e);
};

/**
 * Set a gift message on an existing cart.
 * @param {String} note Gift message
 * @returns {Promise} Resolves with updated Cart contents; rejects if Cart error.
 */
export const setGiftMessage = async (note, apolloClient) => {
  const cartId = await getCartId(apolloClient);
  const country = getUserCountry(apolloClient);

  return safeRequest(
    CART_NOTE_UPDATE,
    {
      cartId,
      note,
      country
    },
    apolloClient
  )
    .then(handleStorefrontRequest('cartNoteUpdate'))
    .then(proxyPublishCartUpdate)
    .then((result) => {
      apolloClient.writeQuery({
        query: NOTE,
        data: {
          cart: {
            id: cartId,
            note,
            __typename: 'Cart'
          }
        }
      });

      return result;
    })
    .catch((e) => e);
};

/**
 *  Updates the Cart object with an array of new attributes and merges them with existing attributes.
 *
 * @param {Object[]} newAttributes array of attributes to be added to the cart object
 * @param {String} newAttributes.key identifier for the custom attribute
 * @param {String} newAttributes.value value of the custom attribute
 * @param apolloClient apollo client
 */
export const setCustomAttributes = async (newAttributes, apolloClient) => {
  const cartId = await getCartId(apolloClient);
  const country = getUserCountry(apolloClient);

  const cart = await safeRequest(GraphQL.CART, {
    cartId,
    country
  }).then(handleStorefrontRequest('cart'));

  const existingAttributes = cart.attributes || [];

  const mergedAttributes = [
    ...existingAttributes.filter(
      (existing) =>
        !newAttributes.some((newAttr) => newAttr.key === existing.key)
    ),
    ...newAttributes
  ];

  return safeRequest(
    CART_ATTRIBUTES_UPDATE,
    {
      cartId,
      attributes: mergedAttributes,
      country
    },
    apolloClient
  )
    .then(handleStorefrontRequest('cartAttributesUpdate'))
    .then(proxyPublishCartUpdate);
};
